import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, DollarSign, TrendingUp, Shield } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function SalaryNegotiation() {
  return (
    <>
      <Helmet>
        <title>Salary Negotiation Tools - Get Paid What You're Worth | LoopBaan.ai</title>
        <meta name="description" content="Negotiate better salaries with data-driven insights, market comparisons, and AI-powered negotiation scripts. Know your worth and get paid fairly." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">NEGOTIATION</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Get Paid{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  What You're Worth
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Armed with real salary data, market insights, and proven negotiation tactics, confidently ask for the compensation you deserve.
              </p>
              <div className="flex justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Start Negotiating <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: DollarSign,
                  title: "Market Salary Data",
                  description: "See real salaries for your role, experience level, and location. Know exactly what the market pays."
                },
                {
                  icon: TrendingUp,
                  title: "Negotiation Scripts",
                  description: "AI generates personalized scripts for every scenario. Practice and perfect your delivery."
                },
                {
                  icon: Shield,
                  title: "Risk-Free Tactics",
                  description: "Learn proven negotiation strategies that protect your offer while maximizing compensation."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">How It Works</h2>
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Research Market Rates",
                    description: "Access real salary data for your specific role, location, and industry. Understand your market value."
                  },
                  {
                    step: "2",
                    title: "Build Your Case",
                    description: "AI helps you compile your achievements, skills, and unique value propositions into a compelling argument."
                  },
                  {
                    step: "3",
                    title: "Practice & Negotiate",
                    description: "Use our AI-generated scripts and practice tools to confidently negotiate your offer and maximize your compensation."
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What You'll Get</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Real salary ranges for your role",
                  "Location-based compensation data",
                  "Benefits and perks breakdown",
                  "Total compensation calculators",
                  "AI-generated negotiation scripts",
                  "Practice conversation scenarios",
                  "Email templates for negotiations",
                  "Expert tips and strategies"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Earn More?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Stop leaving money on the table. Negotiate with confidence using data and proven strategies.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Start Negotiating <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
